/**
 * MARGIN TRADING — Conditional dropdown (Limit / Market / Conditional strip)
 * =============================================================================
 * Each `id` is stored in `infoPlaceOrder` on the margin page when the user picks
 * an item from the Conditional menu.
 *
 * - LIMIT / MARKET: top-level tabs (not listed here).
 * - Rows below: Conditional submenu only.
 *
 * `apiSupported === true` → existing spot `/spot/v1/orders` flow in `placeSpotOrder`.
 * `apiSupported === false` → separate React state + `MarginAdvancedFields.jsx` UI;
 *   submit shows “not wired” until backend contracts exist.
 */

/** @typedef {{ id: string, label: string, apiSupported: boolean, note?: string }} MarginConditionalMenuItem */

/** @type {MarginConditionalMenuItem[]} */
export const MARGIN_CONDITIONAL_DROPDOWN_ITEMS = [
  {
    id: "STOP_LIMIT",
    label: "Stop Limit",
    apiSupported: true,
    note: "Classic stop-limit: stop trigger price + limit price. Uses the standard spot order form.",
  },
  {
    id: "STOP_MARKET",
    label: "Stop Market",
    apiSupported: true,
    note: "Classic stop-market: stop trigger price only; executes at best market price.",
  },
];

/** All `infoPlaceOrder` values that keep the Conditional split-button highlighted. */
export const MARGIN_CONDITIONAL_MENU_KIND_SET = new Set(MARGIN_CONDITIONAL_DROPDOWN_ITEMS.map((i) => i.id));

/** Kinds that use the legacy integrated spot order form (price / amount / sliders / FOK…). */
export const MARGIN_CLASSIC_SPOT_FORM_KINDS = new Set(["LIMIT", "MARKET", "STOP_LIMIT", "STOP_MARKET"]);

/** Kinds that may call `AuthService.placeOrder` today. */
export const MARGIN_SPOT_API_ORDER_KINDS = new Set(["LIMIT", "MARKET", "STOP_LIMIT", "STOP_MARKET"]);

export function marginConditionalMenuLabel(orderKind) {
  const hit = MARGIN_CONDITIONAL_DROPDOWN_ITEMS.find((x) => x.id === orderKind);
  return hit?.label ?? "Conditional";
}

/** Conditional uses `MarginConditionalFields`, not the classic stop row. */
export function marginOrderUsesStopTriggerField(orderKind) {
  return orderKind === "STOP_LIMIT" || orderKind === "STOP_MARKET";
}

export function marginOrderUsesMarketPriceReadonly(orderKind) {
  return orderKind === "MARKET" || orderKind === "STOP_MARKET";
}

/** Amount denom selector (BASE vs QUOTE) — same rules as classic stop-market/market. */
export function marginShowAmtDenomSelect(orderKind) {
  return orderKind === "MARKET" || orderKind === "STOP_MARKET" || orderKind === "STOP_LIMIT";
}
